import React, { useState, useEffect } from "react";
import qs from "query-string";
import moment from "moment";
import { useSelector } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { object, string, number, array, bool, date } from "yup";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { Row, Card, Form, Space, Spinner } from "@masterteam/mt-design-react";

import api from "../../providers/api";
import { addToast, findUnitLabel, cleanKPIMeasures } from "../../helpers";

import Steps from "../Steps";
import Header from "../KPIChangeDetails/Header";
import History from "../KPIChangeDetails/History";
import FormHeader from "../FormHeader";
import RequestDetails from "../KPIChangeDetails/RequestDetails";
import ConfirmationModal from "../ConfirmationModal";
import ChangeParameters from "./ChangeParameters";

import "./style.scss";

const generateFormulaRegex = (parameters = []) =>
  new RegExp(
    `\\+|-|\\*|\\/|100%${parameters.reduce(
      (acc, item) => `${acc}|${item.ParameterName}`,
      ""
    )}`,
    "g"
  );

const validateFormula = async (Formula, Parameters) => {
  try {
    const { Data } = await api.kpi.validateFormula({
      Parameters,
      Formula
    });
    return Data;
  } catch {
    return false;
  }
};

const validationSchema = () =>
  object().shape({
    RequestDescription: string().required(" "),
    Parameters: array()
      .of(
        object().shape({
          ParameterName: string().required(" "),
          Description: string().required(" ")
        })
      )
      .required(" "),
    Formula: array()
      .of(string().required(" "))
      .required(" ")
      .when(["Parameters"], (parameters, schema) => {
        if (parameters?.length) {
          return schema.test("validate-formula", " ", async (value) => {
            return await validateFormula(value?.join(""), parameters);
          });
        }
      })
  });

const KPIChangeParameters = ({ location }) => {
  const intl = useIntl();
  const { kpi, type } = qs.parse(location.search);
  const { id } = useParams();
  const history = useHistory();
  const [kpiDetails, setKpiDetails] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [kpiDetailsLoading, setKpiDetailsLoading] = useState(false);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [
    KPIChangeType,
    user,
    objectiveLevels,
    polarities,
    frequencies,
    calculationMethods
  ] = useSelector(({ theme, user }) => [
    theme.ChangeRequestChangeType.item?.Items,
    user.user,
    theme?.ObjectiveLevel?.item?.Items,
    theme.Polarities.item.Items,
    theme.Frequencies.item.Items,
    theme.CalculationMethods.item.Items
  ]);

  const [lists, setLists] = useState({
    users: [],
    themes: [],
    pillars: []
  });

  const [initialValues, setInitialValues] = useState({
    ChangeType: type,
    CreatedBy: user.DisplayName,
    Created: new Date(),
    RequestDescription: "",
    Parameters: [
      {
        ParameterName: "",
        Description: ""
      }
    ],
    Formula: []
  });

  const getData = async () => {
    setIsLoading(true);
    try {
      if (id) {
        await getRequestData();
      }

      setIsLoading(false);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getKpiDetails = async (id) => {
    setKpiDetailsLoading(true);
    let kpiRes;

    try {
      kpiRes = await api.kpi.getById(id);

      if (kpiRes?.StatusCode === "Success") {
        const CalculationMethod = calculationMethods.find(
          (item) => parseInt(item.Value) === kpiRes?.Data.CalculationMethod
        );

        let formula = [];
        if (kpiRes?.Data.Formula) {
          const regex = generateFormulaRegex(kpiRes?.Data.Parameters);
          formula = kpiRes?.Data.Formula.match(regex);
        }

        setInitialValues({
          ...initialValues,
          Formula: formula,
          CalculationMethod,
          Parameters: kpiRes?.Data?.Parameters || [
            {
              ParameterName: "",
              Description: ""
            }
          ]
        });
        setKpiDetails(kpiRes.Data);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setKpiDetailsLoading(false);
    }

    if (kpiRes?.StatusCode === "Success") {
      return kpiRes.Data;
    }
  };

  const getRequestData = async () => {
    setRequestIsLoading(true);
    try {
      const res = await api.workflow.getRequestByID(id);
      const Form = res?.Data?.Form;

      setRequestData(res?.Data);
      setIsReadOnly(res?.Data?.Status !== 7);

      const CalculationMethod = calculationMethods.find(
        (item) => parseInt(item.Value) === Form?.KPIDetails?.CalculationMethod
      );

      let formula = [];
      if (Form?.KPIDetails.Formula) {
        const regex = generateFormulaRegex(Form?.KPIDetails.Parameters);
        formula = Form?.KPIDetails.Formula.match(regex);
      }
      setKpiDetails(Form?.OldKPIDetails);
      setInitialValues({
        ...initialValues,
        RequestDescription: Form?.RequestDescription,
        ChangeType: type ?? Form?.ChangeType,
        Formula: formula,
        CalculationMethod,
        Parameters: Form?.KPIDetails?.Parameters || [
          {
            ParameterName: "",
            Description: ""
          }
        ],
        CreatedBy: res?.Data?.RequestedBy?.DisplayName
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData"
      });
    } finally {
      setRequestIsLoading(false);
    }
  };

  const onSubmit = async (values, isDraft = false) => {
    setIsSubmitting(true);
    try {
      let attachments = [];

      if (values?.Attachments?.length) {
        const attachmentsRes = await api.attachment.create(values?.Attachments);
        attachments = attachmentsRes?.Data?.map((item) => ({ ID: item }));
      }

      const body = {
        RequestID: id || 0,
        KPIID: kpiDetails?.ID,
        RequestDescription: values?.RequestDescription,
        ChangeType: values?.ChangeType,
        IsDraft: isDraft,
        KPIDetails: {
          ...values,
          Formula: values.Formula?.join("") || undefined,
          CalculationMethod: values.CalculationMethod?.Value
        },
        OldKPIDetails: { ...kpiDetails },
        Attachments: attachments
      };

      const res = await api.kpi.changeKPIRequest(body);

      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.sendOperation");
        history.push(`/splus/kpi-details/${kpiDetails?.ID}`);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function fetchData() {
    getData();
    if (kpi) getKpiDetails(kpi);
  }

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoading || kpiDetailsLoading || requestIsLoading)
    return (
      <Row justify="center" style={{ marginTop: "60px" }}>
        <Spinner size="large" />
      </Row>
    );

  return (
    <>
      <Header kpiDetails={kpiDetails} />
      <Card className="kpi-change-request">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema(
            kpiDetails?.Objective?.StrategyType
          )}
          onSubmit={() => {
            setIsConfirmationModalOpen(true);
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <>
              <FormHeader
                canApprove={requestData?.CanApprove}
                isReadOnly={isReadOnly}
                getData={() => {
                  getRequestData();
                }}
                data={requestData}
                title="change-request-form"
                cancelRoute={`/splus/kpi-details/${kpiDetails?.ID}`}
                handleSaveAsDraft={() => {
                  onSubmit(values, true);
                }}
              />

              {isReadOnly ? <Steps steps={requestData?.Steps} /> : null}

              <Space className="form-body" direction="vertical" size="large">
                <Space className="request-type-wrapper">
                  <div className="label">
                    <FormattedMessage id="change-type" /> :
                  </div>
                  <div className="request-type">
                    <FormattedMessage id="change-kpi-parameters" />
                  </div>
                </Space>

                <RequestDetails
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                />

                <ChangeParameters
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                  lists={lists}
                  values={values}
                  setFieldValue={setFieldValue}
                />

                {isReadOnly ? <History data={requestData?.WFHistory} /> : null}
              </Space>

              {isConfirmationModalOpen && (
                <ConfirmationModal
                  isOpen={isConfirmationModalOpen}
                  title={intl.formatMessage({ id: "kpi-change-request" })}
                  closeModal={() => {
                    setIsConfirmationModalOpen(false);
                    addToast.error("toast.cancelOperation");
                  }}
                  handleSubmit={() => {
                    onSubmit(values, false);
                  }}
                  ButtonStatus={isSubmitting}
                />
              )}
            </>
          )}
        </Form>
      </Card>
    </>
  );
};

export default withRouter(KPIChangeParameters);
